// @flow
import type { FieldProps } from 'redux-form';
import React from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { MdCheck } from 'react-icons/lib/md';

import type { ID_TYPE } from '../../../types/common';
import * as selectors from '../../../reducers';
import * as studentActions from '../../../actions/students';
import Button from '../../Button';
import PopupContent from '../../PopupContent';
import PopupFooter from '../../PopupFooter';
import PopupCloseButton from '../../PopupCloseButton';
import RouteStopsScheduler from '../RouteStopsScheduler';
import WeekHeader from '../WeekHeader';
import StudentScheduleLoader from '../StudentScheduleLoader';
import { Cancel } from '../../CommonTexts';
import styles from './studentSchedulingForm.css';


const routeStopsSchedulerFieldRenderer = ({
  input,
  meta,
  ...props
}: FieldProps) => (
  <RouteStopsScheduler {...input} {...meta} {...props} />
);

type StudentSchedulingFormPropTypes = {
  studentId: ID_TYPE,
  isSaving?: boolean,
  handleSubmit: Function,
};

type StudentSchedulingContainerPropTypes = {
  studentId: ID_TYPE,
  isLoading?: boolean,
};

const StudentSchedulingFormLoading = () => (
  <PopupContent clearPaddings>
    <table className={styles.calendarTable}>
      <tbody>
        <StudentScheduleLoader />
      </tbody>
    </table>
  </PopupContent>
);

const StudentSchedulingFormDummy = ({
  studentId,
  handleSubmit,
  isSaving = false,
}: StudentSchedulingFormPropTypes) => (
  <form onSubmit={handleSubmit}>
    <PopupContent clearPaddings className={styles.scheduleContent}>
      <table className={styles.calendarTable}>
        <tbody>
          <WeekHeader studentId={studentId} />
          <Field
            name="going"
            studentId={studentId}
            routeType="going"
            title="Recolección:"
            component={routeStopsSchedulerFieldRenderer}
          />
          <Field
            name="returning"
            studentId={studentId}
            routeType="returning"
            title="Entrega:"
            component={routeStopsSchedulerFieldRenderer}
          />
        </tbody>
      </table>
    </PopupContent>
    <PopupFooter>
      <PopupCloseButton title={Cancel()} tint="paleGray" />
      <Button
        type="submit"
        tint="primary"
        title="Guardar"
        Icon={MdCheck}
        isLoading={isSaving}
        clearBottomMargin
      />
    </PopupFooter>
  </form>
);
const GATEWAY_TYPES_ENTRANCE = 'entry';
const GATEWAY_TYPES_EXIT = 'exit';
const ROUTE_TYPE_GOING = 'going';
const ROUTE_TYPE_RETURNING = 'returning';
const StudentSchedulingForm = connect(
  (state, { studentId }) => {
    const { weekRouteStopSchedule = {} } = selectors.getStudent(state, studentId);
    const gatewayUserActions = selectors.getStudentGatewayUserActions(state, studentId);
    // Build initial values (group by route type and gateway type)
    const initialValues = {};
    Object.keys(weekRouteStopSchedule).forEach(
      (stopId) => {
        const { route } = selectors.getRouteStop(state, stopId);
        const { route_type } = selectors.getRoute(state, route);

        if (initialValues[route_type]) {
          initialValues[route_type][stopId] = {
            ...weekRouteStopSchedule[stopId],
            type: 'route',
          };
        } else {
          initialValues[route_type] = {
            [stopId]: {
              ...weekRouteStopSchedule[stopId],
              type: 'route',
            },
          };
        }
      },
    );

    gatewayUserActions.forEach(
      (elm) => {
        if (elm.action === GATEWAY_TYPES_ENTRANCE) {
          if (initialValues[ROUTE_TYPE_GOING]) {
            initialValues[ROUTE_TYPE_GOING][`gg_${elm.gateway.id}`] = {
              ...elm,
              id: `gg_${elm.gateway.id}`,
              gatewayId: elm.gateway.id,
              gatewayUserAction: elm.id,
              week_days: elm.schedule.week_days,
              type: 'gateway',
            };
          } else {
            initialValues[ROUTE_TYPE_GOING] = {
              [`gg_${elm.gateway.id}`]: {
                ...elm,
                id: `gg_${elm.gateway.id}`,
                gatewayId: elm.gateway.id,
                gatewayUserAction: elm.id,
                week_days: elm.schedule.week_days,
                type: 'gateway',
              },
            };
          }
        }
        if (elm.action === GATEWAY_TYPES_EXIT) {
          if (initialValues[ROUTE_TYPE_RETURNING]) {
            initialValues[ROUTE_TYPE_RETURNING][`gr_${elm.gateway.id}`] = {
              ...elm,
              id: `gr_${elm.gateway.id}`,
              gatewayId: elm.gateway.id,
              gatewayUserAction: elm.id,
              week_days: elm.schedule.week_days,
              type: 'gateway',
            };
          } else {
            initialValues[ROUTE_TYPE_RETURNING] = {
              [`gr_${elm.gateway.id}`]: {
                ...elm,
                id: `gr_${elm.gateway.id}`,
                gatewayId: elm.gateway.id,
                gatewayUserAction: elm.id,
                week_days: elm.schedule.week_days,
                type: 'gateway',
              },
            };
          }
        }
      },
    );
    return {
      isSaving: selectors.isSavingRouteStopAssignations(state),
      initialValues,
    };
  },
  undefined,
)(
  reduxForm({
    onSubmit(values, dispatch, { studentId }) {
      // Check if has Conflicts
      let error;
      if (values.going) {
        const allWeekDays = [];
        Object.keys(values.going).forEach((id) => {
          const current = values.going[id];
          current.week_days.forEach((day) => {
            if (allWeekDays.filter(d => d === day).length > 0) {
              error = { week_days: 'Conflict' };
            }
            allWeekDays.push(day);
          });
        });
      }
      if (values.returning) {
        const allWeekDays = [];
        Object.keys(values.returning).forEach((id) => {
          const current = values.returning[id];
          current.week_days.forEach((day) => {
            if (allWeekDays.filter(d => d === day).length > 0) {
              error = { week_days: 'Conflict' };
            }
            allWeekDays.push(day);
          });
        });
      }
      if (error) {
        throw new SubmissionError({
          week_days: 'Week days conflict',
          _error: 'conflict!',
        });
      }
      // Build new schedule shape
      let newScheduleRoutesStops = {};
      let newScheduleGateways = {};
      Object.keys(values).forEach(
        (routeType) => {
          const current = values[routeType];
          Object.keys(current).forEach(
            (id) => {
              const element = values[routeType][id];
              if (element.type === 'route') {
                newScheduleRoutesStops = {
                  ...newScheduleRoutesStops,
                  [id]: values[routeType][id],
                };
              }
            },
          );
        },
      );
      Object.keys(values).forEach(
        (routeType) => {
          const current = values[routeType];
          Object.keys(current).forEach(
            (id) => {
              const element = values[routeType][id];
              if (element.type === 'gateway') {
                newScheduleGateways = {
                  ...newScheduleGateways,
                  [id]: values[routeType][id],
                };
              }
            },
          );
        },
      );
      dispatch(
        studentActions.startSavingRouteStopSchedule(
          studentId,
          newScheduleRoutesStops,
          newScheduleGateways,
        ),
      );
    },
  })(StudentSchedulingFormDummy),
);


const StudentSchedulingFormContainer = ({
  studentId,
  isLoading = true,
}: StudentSchedulingContainerPropTypes) => (
  isLoading
    ? <StudentSchedulingFormLoading />
    : <StudentSchedulingForm form={`schedulingForm${studentId}`} studentId={studentId} />
);


export default connect(
  (state, { studentId }) => ({
    isLoading: (selectors.isStudentFetching(state, studentId)
      || selectors.getGatewayUserActionIsFetching(state)
      || selectors.isStudentFetchingGateways(state, studentId)),
  }),
  undefined,
)(StudentSchedulingFormContainer);



// WEBPACK FOOTER //
// ./src/scripts/components/StudentSchedulingPopup/StudentSchedulingForm/index.jsx